import {
  DiscussionContextType,
  DiscussionType,
  MessageCreate,
  MessageSendResponse,
  MessageUpdate,
  QUERY_KEYS,
} from "@cnx/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import MessageService from "services/MessageService";
import { useSocket } from "src/socket/useSocket";
import { useAuthStore } from "stores/authStore";
import { showError } from "utils/index";

export const useMessages = ({
  discussionId,
  enabled = true,
}: {
  discussionId?: string;
  enabled?: boolean;
}) => {
  const { t } = useTranslation("messages");
  const { error, data, isPending, isFetching, isSuccess } = useQuery({
    queryKey: [QUERY_KEYS.MESSAGES, discussionId],

    queryFn: () => {
      return MessageService.fetchMessagesByDiscussionId({ discussionId });
    },
    enabled: enabled && Boolean(discussionId),
    placeholderData: undefined,
  });

  useEffect(() => {
    if (!enabled || !error) return;
    showError(t("fetchFailed"));
  }, [enabled, error, t]);

  return { messages: data ?? [], isPending, isFetching, isSuccess };
};

export const useCreateMessageMutation = () => {
  const { t } = useTranslation("messages");
  const queryClient = useQueryClient();
  const socket = useSocket();
  const currentUserId = useAuthStore(state => state.currentUserId);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      body,
      attachments,
    }: {
      body: MessageCreate;
      attachments?: Blob[];
      socketPayload: {
        discussionType: DiscussionType;
        context?: DiscussionContextType;
        message: string;
      };
    }) => MessageService.addMessage(body, attachments || []),

    onSuccess: async (response, { body, socketPayload }) => {
      const discussionId =
        (response?.data as MessageSendResponse | undefined)?.discussionId ??
        body.discussionId;

      if (discussionId) {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.MESSAGES, discussionId],
        });
        socket?.emit("send_message", {
          senderId: currentUserId,
          discussionId,
          ...socketPayload,
        });
      }
    },

    onError: () => showError(t("saveFailed")),
  });
  return { mutateAsync, isPending };
};

export const useUpdateMessageMutation = () => {
  const { t } = useTranslation("messages");
  const queryClient = useQueryClient();
  const socket = useSocket();
  const currentUserId = useAuthStore(state => state.currentUserId);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      body,
      id,
      discussionId: _,
    }: {
      body: MessageUpdate;
      id: string;
      discussionId: string;
    }) => MessageService.editMessage(body, id),

    onSuccess: async (_, { discussionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MESSAGES, discussionId],
      });
      socket?.emit("edit_message", { senderId: currentUserId, discussionId });
    },

    onError: () => showError(t("saveFailed")),
  });
  return { mutateAsync, isPending };
};

export const useDeleteMessagesMutation = () => {
  const { t } = useTranslation("messages");
  const queryClient = useQueryClient();
  const socket = useSocket();
  const currentUserId = useAuthStore(state => state.currentUserId);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      ids,
      discussionId: _,
    }: {
      ids: string[];
      discussionId: string;
    }) => MessageService.deleteMessages(ids),

    onSuccess: async (_, { discussionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MESSAGES, discussionId],
      });
      socket?.emit("delete_message", { senderId: currentUserId, discussionId });
    },

    onError: () => showError(t("deleteFailed")),
  });
  return { mutateAsync, isPending };
};

export const useDeleteMessageMutation = () => {
  const { t } = useTranslation("messages");
  const queryClient = useQueryClient();
  const socket = useSocket();
  const currentUserId = useAuthStore(state => state.currentUserId);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({
      id,
      discussionId: _,
    }: {
      id: string;
      discussionId: string;
    }) => MessageService.deleteMessage(id),

    onSuccess: async (_, { discussionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.MESSAGES, discussionId],
      });
      socket?.emit("delete_message", { senderId: currentUserId, discussionId });
    },

    onError: () => showError(t("deleteFailed")),
  });
  return { mutateAsync, isPending };
};
